import axiosClient from "api/axiosInstance";
import { ClientData, Clients, ClientReport } from "api/models/Clients";

export const getClients = async (
  page: number,
  size: number,
  filters: { [key: string]: string } = {},
  query?: string,
): Promise<Clients> => {
  const client = axiosClient();

  const queryParams = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
    ...filters,
  }).toString();

  const stringQuery = query ? "&query=" + query : "";

  const response = await client.get(`/clients?${queryParams}${stringQuery}`);

  return response.data;
};

export const getClient = async (clientId: string): Promise<Clients> => {
  const client = axiosClient();

  const response = await client.get(`/clients/${clientId}`);

  return response.data;
};

export const updateClient = async (
  clientId: number,
  data: any,
): Promise<Clients> => {
  const client = axiosClient();

  const response = await client.put(`/clients/${clientId}`, data);

  return response.data;
};

export const findClient = async (data: any): Promise<ClientData> => {
  const client = axiosClient();

  const response = await client.post(`/clients/find`, data);

  return response.data;
};

export const createClient = async (data: any): Promise<ClientData> => {
  const client = axiosClient();

  const response = await client.post(`/clients`, data);

  return response.data;
};

export const loadMeteringPoints = async (
  clientId: number,
  legalConsent?: boolean,
): Promise<Clients> => {
  const client = axiosClient();

  const response = await client.get(
    `/clients/${clientId}/load-metering-points${legalConsent ? "?legalConsent=true" : ""}`,
  );

  return response.data;
};

export const getClientMeteringPoints = async (
  clientId: number,
  page: number,
  size: number,
): Promise<Clients> => {
  const client = axiosClient();

  const response = await client.get(
    `/clients/${clientId}/metering-points?page=${page}&size=${size}`,
  );

  return response.data;
};

export const getClientReport = async (
  clientId: number,
  startDate: string,
  endDate: string,
  clientIds: number[],
): Promise<ClientReport> => {
  const client = axiosClient();

  const addClientIds = clientIds.length > 0 ? "&clientIds=" + clientIds : "";

  const response = await client.get(
    `/clients/${clientId}/report?startDate=${startDate}&endDate=${endDate}${addClientIds}`,
  );

  return response.data;
};

export const getClientReportExcelExport = async (
  clientId: number,
  startDate: string,
  endDate: string,
  clientIds: number[],
): Promise<ClientReport> => {
  const client = axiosClient();

  const addClientIds = clientIds.length > 0 ? "&clientIds=" + clientIds : "";

  try {
    const response = await client.get(
      `/clients/${clientId}/report/export?startDate=${startDate}&endDate=${endDate}${addClientIds}`,
      {
        responseType: "blob",
      },
    );

    return response.data; // Return the Blob on success
  } catch (error: any) {
    if (error.response && error.response.data instanceof Blob) {
      const errorText = await error.response.data.text(); // Convert Blob to text
      const errorJson = JSON.parse(errorText); // Parse it as JSON
      throw new Error(errorJson.message); // Explicitly throw an error
    }
  }
};

const adminReportQuery = (
  startDate: string,
  endDate: string,
  clientIds: number[],
  clientGroupId: number | null,
  portfolioId: number | null,
  consumptionForecastId: number | null,
) => {
  const params = new URLSearchParams({ startDate, endDate });
  if (clientIds.length > 0) {
    params.append("clientIds", clientIds.join(","));
  }
  if (clientGroupId) {
    params.append("clientGroupId", clientGroupId.toString());
  }
  if (portfolioId) {
    params.append("portfolioId", portfolioId.toString());
  }
  if (consumptionForecastId) {
    params.append("consumptionForecastId", consumptionForecastId.toString());
  }
  return params.toString();
};

export const getAdminClientReport = async (
  startDate: string,
  endDate: string,
  clientIds: number[],
  clientGroupId: number | null,
  portfolioId: number | null,
  consumptionForecastId: number | null,
): Promise<ClientReport> => {
  const client = axiosClient();

  const response = await client.get(
    `/client-reports?${adminReportQuery(startDate, endDate, clientIds, clientGroupId, portfolioId, consumptionForecastId)}`,
  );

  return response.data;
};

export const getAdminClientReportExcelExport = async (
  startDate: string,
  endDate: string,
  clientIds: number[],
  clientGroupId: number | null,
  portfolioId: number | null,
  consumptionForecastId: number | null,
): Promise<Blob> => {
  const client = axiosClient();

  try {
    const response = await client.get(
      `/client-reports/export?${adminReportQuery(startDate, endDate, clientIds, clientGroupId, portfolioId, consumptionForecastId)}`,
      {
        responseType: "blob",
      },
    );

    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data instanceof Blob) {
      const errorText = await error.response.data.text();
      const errorJson = JSON.parse(errorText);
      throw new Error(errorJson.message);
    }
    throw error;
  }
};

export const uploadFRRBids = async (data: any): Promise<Clients> => {
  const client = axiosClient();

  const response = await client.post(`/frr-bids/upload`, data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
};

export const saveClientSupplyPlanForecast = async (
  clientId: number,
  data: any,
): Promise<Clients> => {
  const client = axiosClient();

  const response = await client.post(
    `/clients/${clientId}/supply-plans/forecast`,
    data,
  );

  return response.data;
};

export const uploadClientSupplyPlanForecast = async (
  clientId: number,
  auctionTypeId: number,
  data: any,
  selectedProductionPark?: number,
): Promise<any> => {
  const client = axiosClient();

  const productionPark = selectedProductionPark
    ? `?productionParkId=${selectedProductionPark}`
    : "";

  const response = await client.post(
    `/clients/${clientId}/supply-plans/forecast/import${productionPark}`,
    { auctionTypeId: auctionTypeId, file: data },
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return response.data;
};

export const getClientSupplyPlanForecast = async (
  clientId: number,
  auctionTypeId: number,
  date: string,
  selectedProductionPark?: number,
): Promise<any> => {
  const client = axiosClient();

  const productionPark = selectedProductionPark
    ? `&productionParkId=${selectedProductionPark}`
    : "";

  const response = await client.get(
    `/clients/${clientId}/supply-plans/forecast?auctionTypeId=${auctionTypeId}&date=${date}${productionPark}`,
  );

  return response.data;
};

export const getClientSupplyPlanPriceLimits = async (
  clientId: number,
  auctionId: number,
  selectedProductionPark?: number,
): Promise<any> => {
  const client = axiosClient();

  const productionPark = selectedProductionPark
    ? `&productionParkId=${selectedProductionPark}`
    : "";

  const response = await client.get(
    `/clients/${clientId}/supply-plans/price-limits?auctionId=${auctionId}${productionPark}`,
  );

  return response.data;
};

export const saveClientSupplyPlanPriceLimits = async (
  clientId: number,
  data: any,
): Promise<any> => {
  const client = axiosClient();

  const response = await client.post(
    `/clients/${clientId}/supply-plans/price-limits`,
    data,
  );

  return response.data;
};

export const getClientFRRBids = async (
  clientId: number,
  filters: {
    uploadedAtFrom?: string | null;
    uploadedAtTo?: string | null;
    processType?: string | null;
    reserve_bid_timeinterval_start?: string | null;
    reserve_bid_timeinterval_end?: string | null;
  },
): Promise<Clients> => {
  const client = axiosClient();

  const params = new URLSearchParams();
  params.append("client_id", clientId.toString());

  if (filters.uploadedAtFrom) {
    params.append("uploaded_at_from", filters.uploadedAtFrom);
  }

  if (filters.uploadedAtTo) {
    params.append("uploaded_at_to", filters.uploadedAtTo);
  }

  if (filters.processType) {
    params.append("process_type", filters.processType);
  }

  if (filters.reserve_bid_timeinterval_start) {
    params.append(
      "reserve_bid_timeinterval_start",
      filters.reserve_bid_timeinterval_start,
    );
  }

  if (filters.reserve_bid_timeinterval_end) {
    params.append(
      "reserve_bid_timeinterval_end",
      filters.reserve_bid_timeinterval_end,
    );
  }

  const response = await client.get(`/frr-bids?${params.toString()}`);

  return response.data;
};

export const getClientGOSettings = async (clientId: number): Promise<any> => {
  const client = axiosClient();

  const response = await client.get(
    `/guarantee-of-origin-settings/client/${clientId}`,
  );

  return response.data;
};

export const saveClientGOSettings = async (postData: any): Promise<any> => {
  const client = axiosClient();

  const response = await client.put(`/guarantee-of-origin-settings`, postData);

  return response.data;
};

export const getGOCertificates = async (
  page: number,
  size: number,
  filters: { [key: string]: string | string[] } = {},
): Promise<any> => {
  const client = axiosClient();

  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("size", size.toString());

  Object.entries(filters).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, v));
    } else if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  });

  const response = await client.get(
    `/guarantee-of-origins?${params.toString()}`,
  );

  return response.data;
};

export const addClientToGOGroup = async (postData: any): Promise<any> => {
  const client = axiosClient();

  const response = await client.put(
    `/guarantee-of-origins/client-groups/add`,
    postData,
  );

  return response.data;
};

export const removeClientFromGOGroup = async (postData: any): Promise<any> => {
  const client = axiosClient();

  const response = await client.put(
    `/guarantee-of-origins/client-groups/remove`,
    postData,
  );

  return response.data;
};

export const getClientGOGroup = async (clientId: number): Promise<any> => {
  const client = axiosClient();

  const response = await client.get(
    `/guarantee-of-origins/client-groups/client/${clientId}`,
  );

  return response.data;
};
